import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { AppContainerComponent } from '../../layout/app-container/app-container.component';
import { HeaderComponent } from '../../layout/header/header.component';
import { GraphsComponent } from '../../features/graphs/graphs.component';
import { ProjectInfoComponent } from '../../features/project-info/project-info.component';
import { ProjectInfoService } from '../../features/project-info/services/project-info.service';
import { catchError, forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import { Recycle } from 'lucide-angular';
import { CommonModule } from '@angular/common';
import { SolutionsComponent } from '../../features/solutions/solutions.component';
import { ComparisonComponent } from '../../features/comparison/comparison.component';
import { WarningMessageComponent } from '../../shared/components/warning-message.component';
import { GraphSeriesSet } from '../../features/graphs/models/graphs.types';
import { Solution, SolutionWithProducts } from '../../features/solutions/models/solutions.types';
import { Product } from '../../features/products/models/products.types';
import { GraphsService } from '../../features/graphs/services/graphs.service';
import { ProductsService } from '../../features/products/services/products.service';
import { SolutionsService } from '../../features/solutions/services/solutions.service';
import { AsyncState, getData, getError } from '../../shared/models/async-state';
import { ProjectInfo } from '../../features/project-info/models/project-info.types';
import { ErrorStateComponent } from '../../shared/components/error-state.component';
import { LoadingSkeletonComponent } from '../../shared/components/loading-skeleton.component';
import { createRetryableAsyncState } from '../../shared/utils/retryable-async-state';
import { Period } from '../../core/constants/periods';

@Component({
  selector: 'app-main-page',
  imports: [
    AppContainerComponent,
    HeaderComponent,
    GraphsComponent,
    ProjectInfoComponent,
    CommonModule,
    SolutionsComponent,
    ComparisonComponent,
    WarningMessageComponent,
    ErrorStateComponent,
    LoadingSkeletonComponent,
  ],
  templateUrl: './main-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainPageComponent {
  readonly projectId = input.required<string>();

  private readonly projectInfoService = inject(ProjectInfoService);
  private readonly graphsService = inject(GraphsService);
  private readonly solutionsService = inject(SolutionsService);
  private readonly productsService = inject(ProductsService);

  readonly selectedPeriod = signal<Period>('DAY');

  onPeriodChange(period: Period): void {
    this.selectedPeriod.set(period);
  }

  private projectInfoRetry$ = new Subject<void>();
  private graphsRetry$ = new Subject<void>();
  private solutionsRetry$ = new Subject<void>();

  readonly projectInfoState = createRetryableAsyncState<ProjectInfo>({
    idSignal: this.projectId,
    retry$: this.projectInfoRetry$,
    fetcher: id => this.projectInfoService.getProjectInfo(id),
  });

  readonly category = computed(() => getData(this.projectInfoState())?.category ?? undefined);
  readonly projectInfoData = computed(() => getData(this.projectInfoState()));
  readonly projectInfoError = computed(() => getError(this.projectInfoState()));

  retryProjectInfo(): void {
    this.projectInfoService.invalidate(this.projectId());
    this.projectInfoRetry$.next();
  }

  readonly graphsState = createRetryableAsyncState<GraphSeriesSet>({
    idSignal: this.projectId,
    periodSignal: this.selectedPeriod,
    retry$: this.graphsRetry$,
    fetcher: (id, period) => this.graphsService.getAllSeries(id, period),
  });

  retryGraphs(): void {
    this.graphsRetry$.next();
  }

  readonly hasAnyGraphsData = computed(() => {
    const data = getData(this.graphsState());
    if (!data) {
      return false;
    }
    return (data.load?.length ?? 0) > 0 || (data.soc?.length ?? 0) > 0 || (data.powerInput?.length ?? 0) > 0;
  });

  readonly solutionsWithProductsState = createRetryableAsyncState<SolutionWithProducts[]>({
    idSignal: this.projectId,
    periodSignal: this.selectedPeriod,
    retry$: this.solutionsRetry$,
    fetcher: (id, period) => this.fetchSolutionsWithProducts(id, period),
  });

  readonly solutionsState = computed<AsyncState<Solution[]>>(() => {
    const state = this.solutionsWithProductsState();
    if (state.status !== 'success') {
      return state;
    }
    return { status: 'success', data: state.data.map(swp => swp.solution) };
  });

  private fetchSolutionsWithProducts(id: string, period: Period): Observable<SolutionWithProducts[]> {
    return this.solutionsService.getSolutions(id, period).pipe(
      switchMap(solutions => {
        if (solutions.length === 0) {
          return of([] as SolutionWithProducts[]);
        }

        const productRequests = solutions.map(solution =>
          this.productsService.getProducts(id, solution.id, period).pipe(
            catchError(err => {
              console.warn(`[Products] Failed to load products for solution ${solution.id}:`, err?.message);
              return of([]);
            }),
            map(products => ({ solution, products: this.enrichProducts(solution, products) })),
          ),
        );
        return forkJoin(productRequests);
      }),
    );
  }

  private enrichProducts(solution: Solution, products: Product[]): Product[] {
    if (solution.economizer === null) {
      return products;
    }
    return products.map(product => ({
      ...product,
      properties: [
        ...product.properties,
        { label: 'Economizer', value: solution.economizer ? 'Yes' : 'No', icon: Recycle },
      ],
    }));
  }

  retrySolutions(): void {
    this.solutionsRetry$.next();
  }
}
